import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Avatar,
  Box,
  Button,
  Hide,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Td,
  Text,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import NextLink from 'next/link';
import * as React from 'react';

import { content_shared } from '@/analytics';
import {
  CopyIcon,
  Edit2Icon,
  LinkIcon,
  MoreHorizontalIcon,
  MoveIcon,
  TrashIcon,
} from '@/imports/ui/chakra/feather';
import { BulkMoveLibraryLinksModal } from '@/imports/ui/components/resource/BulkMoveLibraryLinksModal';
import { ResourceListDragHandle } from '@/imports/ui/components/resource/ResourceListDragHandle';
import {
  ResourceListName,
  resourceListDateTdProps,
  resourceListNameTdProps,
  resourceListNameTextProps,
} from '@/imports/ui/components/resource/ResourceListName';
import {
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';

import { EditLinkModal } from '../../AddLinkModal/AddLinkModal';

type ILink = {
  _id: string;
  name: string;
  url: string;
  dateCreated?: string | null;
  createdBy?: {
    _id: string;
    displayName: string;
    proxyProfilePictureUrl?: string | null;
  } | null;
};

type IResourceListLinkProps = {
  link: ILink;
  canEditGroupLibrary: boolean;
  groupId: string;
  rootFolderId: string;
  onAfterMove: () => void;
  onAfterDelete: () => void;
  onAfterRename: () => void;
  isReorderEnabled?: boolean;
  dragOptions?: {
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
  };
};

const DELETE_LINKS_AST = gql`
  mutation ResourceListLink_deleteGroupLibraryLinks(
    $input: DeleteGroupLibraryLinksInput!
  ) {
    deleteGroupLibraryLinks(input: $input)
  }
`;

export const ResourceListLink: React.FC<IResourceListLinkProps> & {
  linkFragment: DocumentNode;
} = ({
  link,
  canEditGroupLibrary,
  groupId,
  rootFolderId,
  onAfterMove,
  onAfterDelete,
  onAfterRename,
  isReorderEnabled = false,
  dragOptions,
}) => {
  const toast = useToast();
  const moveModal = useDisclosure();
  const renameModal = useDisclosure();
  const [deleteLinks] = useMutation(DELETE_LINKS_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const onClickOpen = (): void => {
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const onClickCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(link.url);
      content_shared({
        entity_id: link._id,
        entity_type: 'resource_link',
        content_id: link._id,
        content_type: 'resource_link',
        group_id: groupId,
        share_method: 'copy_link',
      });
      toast({ status: 'success', description: 'Copied link URL.' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to copy URL.' });
    }
  };

  const onClickDelete = async (): Promise<void> => {
    try {
      await deleteLinks({
        variables: { input: { groupId, linkIds: [link._id] } },
      });
      onAfterDelete();
      toast({ status: 'success', description: 'Deleted link.' });
    } catch (err) {
      toast({ status: 'error', description: 'Failed to delete link.' });
    }
  };

  return (
    <Tr
      ref={dragOptions?.provided.innerRef}
      {...dragOptions?.provided.draggableProps}
      _hover={{ bg: 'gray.50' }}
      minH="4em"
      bg={dragOptions?.snapshot.isDragging ? 'blue.50' : undefined}
    >
      {canEditGroupLibrary && (
        <Td w="8" pr="0">
          <ResourceListDragHandle
            isEnabled={isReorderEnabled}
            dragHandleProps={dragOptions?.provided.dragHandleProps}
          />
        </Td>
      )}
      {canEditGroupLibrary && <Td />}
      <Td {...resourceListNameTdProps}>
        <Tooltip hasArrow label={link.url} placement="top">
          <ResourceListName icon={<LinkIcon boxSize={5} color="gray.500" />}>
            <Button
              {...resourceListNameTextProps}
              variant="link"
              colorScheme="blue"
              onClick={onClickOpen}
              display="block"
              height="auto"
              minW={0}
              w="full"
              textAlign="left"
            >
              {link.name}
            </Button>
          </ResourceListName>
        </Tooltip>
      </Td>
      <Hide below="lg">
        <Td {...resourceListDateTdProps}>
          <Text textStyle="compact">
            {link.dateCreated
              ? dayjs(link.dateCreated).format('MMM D, YYYY')
              : '--'}
          </Text>
        </Td>
        <Td>
          {link.createdBy ? (
            <Tooltip label={link.createdBy.displayName} hasArrow>
              <Box display="inline-block" pl="2">
                <NextLink href={`/members/${link.createdBy._id}`}>
                  <Avatar
                    size="sm"
                    name={link.createdBy.displayName}
                    src={link.createdBy.proxyProfilePictureUrl || undefined}
                    onClick={(e) => e.stopPropagation()}
                  />
                </NextLink>
              </Box>
            </Tooltip>
          ) : (
            <Text textStyle="compact" color="gray.500">
              --
            </Text>
          )}
        </Td>
      </Hide>
      <Td>
        {canEditGroupLibrary && (
          <>
            <Menu aria-label="link actions">
              <MenuButton
                as={IconButton}
                variant="outline"
                colorScheme="gray"
                size="sm"
                icon={<MoreHorizontalIcon />}
              />
              <Portal>
                <MenuList>
                  <MenuItem
                    icon={<MoveIcon color="gray.600" />}
                    onClick={moveModal.onOpen}
                  >
                    Move
                  </MenuItem>
                  <MenuItem
                    icon={<Edit2Icon color="gray.600" />}
                    onClick={renameModal.onOpen}
                  >
                    Rename
                  </MenuItem>
                  <MenuItem
                    icon={<CopyIcon color="gray.600" />}
                    onClick={onClickCopy}
                  >
                    Copy link URL
                  </MenuItem>
                  <MenuItem
                    icon={<TrashIcon color="gray.600" />}
                    onClick={onClickDelete}
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
            <BulkMoveLibraryLinksModal
              groupId={groupId}
              linkIds={[link._id]}
              open={moveModal.isOpen}
              onClose={moveModal.onClose}
              rootFolderId={rootFolderId}
              onAfterMove={onAfterMove}
            />
            <EditLinkModal
              linkId={link._id}
              initialName={link.name}
              initialUrl={link.url}
              isOpen={renameModal.isOpen}
              onClose={renameModal.onClose}
              onCompleted={() => {
                onAfterRename();
              }}
            />
          </>
        )}
      </Td>
    </Tr>
  );
};

ResourceListLink.linkFragment = gql`
  fragment ResourceListLink_LinkFragment on LibraryFolderLink {
    _id
    name
    url
    dateCreated
    position
    createdBy {
      _id
      displayName
      proxyProfilePictureUrl(
        input: { w: 40, h: 40, fit: "facearea", facepad: 10.0 }
      )
    }
  }
` as unknown as DocumentNode;
